/** @file Mix preview runs on core's AnimState: from loops, crossfades to `to`, holds, starts over. */
import { clamp } from '@pose2d/math/clamp';
import { createState } from '@pose2d/core/state/createState';
import { setAnimation } from '@pose2d/core/state/setAnimation';
import { stepState } from '@pose2d/core/state/stepState';
import type { AnimState, SkeletonData } from '@pose2d/core/types';
import type { MixRun } from './mixPreviewStore';

/** `from` plays alone for its duration clamped to [LEAD_MIN, LEAD_MAX] seconds before the switch. */
const LEAD_MIN = 0.5;
const LEAD_MAX = 2;
/** Seconds `to` keeps playing after the crossfade before the preview starts over. */
const TAIL = 1;

/** A state with `from` looping on track 0. */
function fresh(data: SkeletonData, from: string): AnimState {
  const state = createState(data);
  setAnimation(state, data, 0, from, { loop: true });
  return state;
}

/** stepState by dt (>= 0), dropping the events (the preview shows poses only). */
function advance(run: MixRun, data: SkeletonData, dt: number): void {
  stepState(run.state, data, dt, run.events);
  run.events.length = 0;
}

/** Mix preview operations; a run is plain data the store holds and step mutates. */
export const mixPreview = {
  /** Crossfade seconds core itself picks for from → to (data.mixDefaults, most specific pair first, §5.7). */
  defaultSeconds(data: SkeletonData, from: string, to: string): number {
    const state = fresh(data, from);
    stepState(state, data, 0, []);
    return setAnimation(state, data, 0, to).mixDuration;
  },
  /** A new run, or null when either name is missing from `data`; `seconds` null takes defaultSeconds. */
  start(data: SkeletonData, from: string, to: string, seconds: number | null): MixRun | null {
    const anim = data.animationIndex.get(from);
    if (anim === undefined || !data.animationIndex.has(to)) return null;
    const mix = seconds ?? mixPreview.defaultSeconds(data, from, to);
    const lead = clamp(data.animations[anim]?.duration ?? 0, LEAD_MIN, LEAD_MAX);
    return { state: fresh(data, from), data, from, to, mix, lead, elapsed: 0, switched: false, events: [] };
  },
  /** The same pair and mix started on `data` and fast-forwarded to `run.elapsed` (null when a name is gone). */
  rebuild(run: MixRun, data: SkeletonData): MixRun | null {
    const next = mixPreview.start(data, run.from, run.to, run.mix);
    if (next !== null) mixPreview.step(next, data, run.elapsed);
    return next;
  },
  /** Advances by dt seconds: switches to `to` exactly at the lead, starts over after lead + mix + TAIL. */
  step(run: MixRun, data: SkeletonData, dt: number): void {
    run.data = data;
    if (run.elapsed + dt >= run.lead + run.mix + TAIL) {
      run.state = fresh(data, run.from);
      run.elapsed = 0;
      run.switched = false;
      return;
    }
    const untilSwitch = run.switched ? Number.POSITIVE_INFINITY : run.lead - run.elapsed;
    if (dt >= untilSwitch) {
      advance(run, data, untilSwitch);
      setAnimation(run.state, data, 0, run.to, { loop: true, mixDuration: run.mix });
      run.switched = true;
      advance(run, data, dt - untilSwitch);
    } else {
      advance(run, data, dt);
    }
    run.elapsed += dt;
  },
};
